import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  CONSIGNMENT_STATUS,
  useChangeConsignmentStatusMutation,
  type ConsignmentStatusCode,
  type ConsignmentSummary
} from '@/entities/consignments';
import { getApiErrorMessage, useProjectSiteParams } from '@/shared/lib';
import type { SelectOption } from '@/shared/ui';

import { getStatusNames } from './options';
import {
  ACTION_TARGET_STATUS,
  type ConsignmentStatusChangeValues,
  type ConsignmentStatusTarget
} from './status-actions';

interface UseConsignmentStatusChangeProps {
  consignmentId?: string;
  consignment?: Pick<ConsignmentSummary, 'statusCode' | 'allowedActions' | 'resetTargetStatusCode'>;
}

/**
 * Status select of a single consignment: the current status plus the status each of the backend's `allowedActions`
 * leads to (a reset shows `resetTargetStatusCode`). Picking one opens the reason dialog, which saves through
 * `POST /consignments/{id}/status`.
 */
export function useConsignmentStatusChange({ consignmentId, consignment }: UseConsignmentStatusChangeProps) {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();
  const [target, setTarget] = useState<ConsignmentStatusTarget | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [changeStatus, { isLoading: isSubmitting }] = useChangeConsignmentStatusMutation();

  const currentStatusCode = consignment?.statusCode ?? CONSIGNMENT_STATUS.NR;

  const targets = useMemo<ConsignmentStatusTarget[]>(
    () =>
      (consignment?.allowedActions ?? [])
        .map((action) => ({
          action,
          statusCode: ACTION_TARGET_STATUS[action] ?? consignment?.resetTargetStatusCode ?? null
        }))
        .filter((option) => option.statusCode !== null),
    [consignment]
  );

  const statusOptions = useMemo<SelectOption<ConsignmentStatusCode>[]>(() => {
    const names = getStatusNames(t);
    return [currentStatusCode, ...targets.map((option) => option.statusCode as ConsignmentStatusCode)].map((code) => ({
      value: code,
      label: names[code]
    }));
  }, [t, currentStatusCode, targets]);

  const selectStatus = useCallback(
    (code: ConsignmentStatusCode) => {
      const picked = targets.find((option) => option.statusCode === code);
      if (!picked) return;
      setTarget(picked);
      setIsDialogOpen(true);
    },
    [targets]
  );

  const confirm = useCallback(
    async ({ reason, comment }: ConsignmentStatusChangeValues) => {
      if (!target || !consignmentId || !projectId || !siteId) return;

      try {
        await changeStatus({
          id: consignmentId,
          projectId,
          siteId,
          action: target.action,
          ...(target.action === 'CANCEL' && { cancellationReason: reason }),
          comment
        }).unwrap();
        toast.success(t('newcmr.save.success'));
        setIsDialogOpen(false);
      } catch (error) {
        toast.error(getApiErrorMessage(error, t('newcmr.save.error')));
      }
    },
    [target, consignmentId, projectId, siteId, changeStatus, t]
  );

  return {
    currentStatusCode,
    statusOptions,
    isDisabled: !consignmentId || targets.length === 0,
    target,
    isDialogOpen,
    setIsDialogOpen,
    isSubmitting,
    selectStatus,
    confirm
  };
}
